import { registerDestructor } from '@ember/destroyable';
import Modifier from 'ember-modifier';
import { getScrollParentOrDefault } from 'volta/utils/dom-utils';

import type Owner from '@ember/owner';
import type { ArgsFor } from 'ember-modifier';

interface IsStuckSignature {
  Element: HTMLElement;
  Args: {
    Named: {
      threshold?: number;
      attribute?: string;
      rootMargin?: string;
    };
    Positional: [boolean?];
  };
}

export default class IsStuckModifier extends Modifier<IsStuckSignature> {
  observer: IntersectionObserver | null = null;
  element: Element | null = null;

  constructor(owner: Owner, args: ArgsFor<IsStuckSignature>) {
    super(owner, args);

    registerDestructor(this, () => {
      this.cleanup();
    });
  }

  modify(
    element: Element | null,
    [enabled = true]: [boolean?],
    named: IsStuckSignature['Args']['Named']
  ) {
    // Always cleanup on modify to ensure fresh observer
    this.cleanup();

    if (!element || !enabled) {
      return;
    }

    this.element = element;

    const threshold = named.threshold ?? 1;
    const attribute = named.attribute ?? 'data-is-stuck';
    const rootMargin = named.rootMargin;

    // Find the scroll container automatically
    const scrollContainer = getScrollParentOrDefault(element as HTMLElement);

    this.observer = new IntersectionObserver(
      (entries) => {
        const entry = entries[0];

        if (entry && entry.target instanceof HTMLElement) {
          entry.target.toggleAttribute(attribute, entry.intersectionRatio < 1);
        }
      },
      {
        root: scrollContainer,
        threshold: [threshold],
        ...(rootMargin && { rootMargin })
      }
    );

    this.observer.observe(element);
  }

  cleanup() {
    if (this.observer) {
      this.observer.disconnect();
      this.observer = null;
    }
  }
}

declare module '@glint/environment-ember-loose/registry' {
  export default interface Registry {
    'is-stuck': typeof IsStuckModifier;
  }
}
